// src/components/AcceptInvite.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Auth from './Auth';
import { useTranslation } from 'react-i18next'; // Importa hook

// --- IMPORT MUI ---
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  Alert,
  Paper
} from '@mui/material';

function AcceptInvite({ user }) {
  const { t } = useTranslation('auth');

  const { inviteId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [errorMessage, setErrorMessage] = useState('');

  // useEffect (tradotto)
  useEffect(() => {
    if (!user) {
      setStatus('unauthorized');
      return;
    }

    setStatus('processing');
    
    const acceptInvite = async () => {
      try {
        const functions = getFunctions();
        const acceptInviteCallable = httpsCallable(functions, 'acceptInvite');
        
        const result = await acceptInviteCallable({ inviteId: inviteId });

        if (result.data.status === 'success') {
          setStatus('success');
          setTimeout(() => {
            navigate('/');
          }, 3000);
        }
        
      } catch (error) {
        console.error("Error calling 'acceptInvite':", error);
        setStatus('error');
        if (error.code === 'functions/unauthenticated') {
          setErrorMessage(t('invite.error_unauthenticated'));
        } else if (error.code === 'functions/not-found') {
          setErrorMessage(t('invite.error_not_found'));
        } else if (error.code === 'functions/already-exists') {
          setErrorMessage(t('invite.error_already_exists'));
        } else if (error.code === 'functions/permission-denied') {
          setErrorMessage(t('invite.error_permission_denied'));
        } else {
          setErrorMessage(t('invite.error_unknown'));
        }
      }
    };

    acceptInvite();
    
  }, [user, inviteId, navigate, t]);

  // Contenitore "Tripla A" (invariato)
  const CenteringBox = ({ children }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        p: 3
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          borderRadius: 3,
          width: '100%',
          maxWidth: '500px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center'
        }}
      >
        {children}
      </Paper>
    </Box>
  );

  
  // --- Render (tradotto) ---

  // 1. Non loggato
  if (status === 'unauthorized') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 3
        }}
      >
        <Typography variant="h5" gutterBottom>
          {t('invite.title')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {t('invite.subtitle')}
        </Typography>
        <Auth /> 
      </Box>
    );
  }

  // 2. Processing
  if (status === 'processing' || status === 'loading') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
        <CircularProgress size={60} />
        <Typography variant="h6">{t('invite.verifying')}</Typography>
      </Box>
    );
  }

  // 3. Errore
  if (status === 'error') {
    return (
      <CenteringBox>
        <Alert severity="error" sx={{ width: '100%', mb: 3 }}>
          <Typography variant="h6">{t('invite.error_title')}</Typography>
          {errorMessage}
        </Alert>
        <Button variant="contained" size="large" onClick={() => navigate('/')} sx={{ height: '56px' }}>
          {t('invite.back_button')}
        </Button>
      </CenteringBox>
    );
  }
  
  // 4. Successo
  if (status === 'success') {
     return (
      <CenteringBox>
        <Alert severity="success" sx={{ width: '100%' }}>
          <Typography variant="h6">{t('invite.success_title')}</Typography>
          {t('invite.success_subtitle')}
        </Alert>
      </CenteringBox>
    );
  }

  return null;
}

export default AcceptInvite;